import { useCallback, useId, useMemo, useState } from 'react'

function formatPeriodLabel(period) {
  if (period === 'quarterly') return 'Quarterly'
  if (period === 'annual' || period === 'year') return 'Annual'
  return 'Monthly'
}

function buildCategoryByMetricId(groups) {
  const map = {}
  groups.forEach((g) => {
    g.metrics.forEach((m) => {
      map[m.id] = g.label
    })
  })
  return map
}

function tracksLine(metric) {
  if (!metric) return ''
  if (metric.platform === 'All Platforms' || metric.source === 'Mixed') {
    return `Aggregated performance for this metric across your stack (${metric.source || 'multiple sources'}).`
  }
  return `Performance on ${metric.platform} (${metric.source || 'channel'}).`
}

function outcomeLine(metric) {
  const helper = metric?.helper ? `${metric.helper} ` : ''
  return `${helper}Once active, we compare your live results to your target each period; the Goals tab shows a progress card and whether you are on track, behind, or ahead.`
}

function renderMetricBlock(metricId, categoryByMetricId, goalMetricLookup, formatGoalValue) {
  const m = goalMetricLookup[metricId]
  if (!m) return null
  const periodLabel = formatPeriodLabel(m.defaultPeriod ?? 'monthly')
  const category = categoryByMetricId[metricId]
  const targetDisplay =
    m.defaultTarget != null && typeof formatGoalValue === 'function'
      ? formatGoalValue(m.defaultTarget, m)
      : '—'

  return (
    <li key={metricId} className="goalsTypesReferenceMetric">
      <div className="goalsTypesReferenceMetricTitleRow">
        <span className="goalsTypesReferenceMetricName">{m.name}</span>
        {category ? <span className="goalsTypesReferenceCategory">{category}</span> : null}
      </div>
      <p className="goalsTypesReferenceTracks">
        <span className="goalsTypesReferenceLabel">What it tracks:</span> {tracksLine(m)}
      </p>
      <p className="goalsTypesReferenceTarget">
        <span className="goalsTypesReferenceLabel">Default target:</span> {targetDisplay} · {periodLabel}
      </p>
      <p className="goalsTypesReferenceOutcome">
        <span className="goalsTypesReferenceLabel">Outcome:</span> {outcomeLine(m)}
      </p>
    </li>
  )
}

function PresetAccordionRow({
  domKey,
  title,
  description,
  metricIds,
  categoryByMetricId,
  goalMetricLookup,
  formatGoalValue,
  expanded,
  onToggle,
  idPrefix,
  nameModifierClass = '',
}) {
  const safeKey = String(domKey).replace(/[^a-zA-Z0-9_-]/g, '-')
  const headerId = `${idPrefix}-heading-${safeKey}`
  const panelId = `${idPrefix}-panel-${safeKey}`
  const count = metricIds?.length ?? 0

  return (
    <li
      className={`goalsTypesReferencePreset ${expanded ? 'goalsTypesReferencePreset--expanded' : ''}`}
    >
      <button
        type="button"
        className="goalsTypesReferencePresetToggle"
        id={headerId}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => onToggle(domKey)}
      >
        <span className="goalsTypesReferencePresetToggleMain">
          <span className={`goalsTypesReferencePresetName goalsTypesReferencePresetName--toggle ${nameModifierClass}`.trim()}>
            {title}
          </span>
          <span className="goalsTypesReferencePresetCount">
            {count} metric{count !== 1 ? 's' : ''}
          </span>
        </span>
        <span
          className={`goalsTypesReferencePresetChevron ${expanded ? 'goalsTypesReferencePresetChevron--open' : ''}`}
          aria-hidden
        />
      </button>
      <div
        id={panelId}
        className="goalsTypesReferencePresetPanel"
        role="region"
        aria-labelledby={headerId}
        hidden={!expanded}
      >
        {description ? (
          <p className="goalsTypesReferencePresetDesc">{description}</p>
        ) : null}
        <ul className="goalsTypesReferenceMetricList">
          {(metricIds || []).map((id) =>
            renderMetricBlock(id, categoryByMetricId, goalMetricLookup, formatGoalValue)
          )}
        </ul>
      </div>
    </li>
  )
}

export default function GoalsTypesReference({
  goalPresets,
  customGoals,
  goalMetricGroups,
  goalMetricLookup,
  formatGoalValue,
}) {
  const idPrefix = useId().replace(/:/g, '')
  const categoryByMetricId = useMemo(
    () => buildCategoryByMetricId(goalMetricGroups),
    [goalMetricGroups]
  )

  const [expandedKeys, setExpandedKeys] = useState(() => {
    const s = new Set()
    goalPresets.forEach((p) => s.add(`preset:${p.id}`))
    customGoals.forEach((g) => s.add(`custom:${g.id}`))
    return s
  })

  const toggleKey = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  return (
    <section className="goalsTypesReference" aria-label="Goal types and metrics reference">
      <ul className="goalsTypesReferencePresetList">
        {goalPresets.map((preset) => (
          <PresetAccordionRow
            key={preset.id}
            domKey={`preset:${preset.id}`}
            title={preset.name}
            description={preset.description}
            metricIds={preset.metricIds}
            categoryByMetricId={categoryByMetricId}
            goalMetricLookup={goalMetricLookup}
            formatGoalValue={formatGoalValue}
            expanded={expandedKeys.has(`preset:${preset.id}`)}
            onToggle={toggleKey}
            idPrefix={idPrefix}
          />
        ))}
      </ul>

      {customGoals.length > 0 && (
        <div className="goalsTypesReferenceCustomSection">
          <h3 className="goalsTypesReferenceSectionHeading">Your custom goal sets</h3>
          <p className="goalsTypesReferenceCustomLead">
            Saved custom sets work like presets: each includes the metrics you chose when you created it.
          </p>
          <ul className="goalsTypesReferencePresetList">
            {customGoals.map((goal) => (
              <PresetAccordionRow
                key={goal.id}
                domKey={`custom:${goal.id}`}
                title={`Custom: ${goal.name}`}
                description={null}
                metricIds={goal.metricIds}
                categoryByMetricId={categoryByMetricId}
                goalMetricLookup={goalMetricLookup}
                formatGoalValue={formatGoalValue}
                expanded={expandedKeys.has(`custom:${goal.id}`)}
                onToggle={toggleKey}
                idPrefix={idPrefix}
                nameModifierClass="goalsTypesReferencePresetName--custom"
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
